import React, { useState, useCallback, useRef } from 'react';

import Button from 'components/partials/button/button';
import RadioInput from 'components/partials/radio-input/radio-input';

import CustomLogicDropdown from './custom-logic-dropdown';
import CustomLogicRow from './custom-logic-row';

import { BASELINE_POPULATIONS } from '../matrix-step.constants';

import Icons from 'assets/icons';

type Category = { category: string; [key: string]: unknown };

type PopulationsTabContentProps = {
  baselineSelected: string | null;
  toggleBaseline: (value: string) => void;
  activeCategoryIds: string[];
  activeCategories: Category[];
  optionByCategory: Record<string, string>;
  toggleCategory: (categoryName: string) => void;
  removeCategory: (categoryName: string) => void;
  setOption: (
    categoryName: string,
    optionId: string | number | null | undefined
  ) => void;
  customLogicCategories: Category[];
  onCustomLogicRequested?: () => void;
  isCustomLogicLoading?: boolean;
};

const PopulationsTabContent = ({
  baselineSelected,
  toggleBaseline,
  activeCategoryIds,
  activeCategories,
  optionByCategory,
  toggleCategory,
  removeCategory,
  setOption,
  customLogicCategories,
  onCustomLogicRequested,
  isCustomLogicLoading,
}: PopulationsTabContentProps) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownContainerRef = useRef<HTMLDivElement | null>(null);

  const handleToggleDropdown = useCallback(() => {
    onCustomLogicRequested?.();
    setIsDropdownOpen((prev) => !prev);
  }, [onCustomLogicRequested]);

  const handleCloseDropdown = useCallback(() => {
    setIsDropdownOpen(false);
  }, []);

  return (
    <div className="p-3" data-testid="populations-tab-content">
      <div className="flex flex-col gap-1.5">
        <div className="flex items-center justify-between">
          <span className="text-base font-semibold text-gray-700">
            Baseline populations
          </span>
          <div className="relative" ref={dropdownContainerRef}>
            <Button
              type="button"
              variant="reserve-text"
              size="sm"
              iconBefore={<Icons.Plus />}
              onClick={handleToggleDropdown}
              data-testid="add-custom-logic-button"
            >
              Add custom logic
            </Button>
            {isDropdownOpen && (
              <CustomLogicDropdown
                categories={customLogicCategories}
                activeCategoryIds={activeCategoryIds}
                onToggleCategory={toggleCategory}
                onClose={handleCloseDropdown}
                isLoading={Boolean(isCustomLogicLoading)}
                containerRef={dropdownContainerRef}
              />
            )}
          </div>
        </div>

        <div className="flex flex-col items-start gap-2">
          {BASELINE_POPULATIONS.map((population) => (
            <RadioInput
              key={population.value}
              labelFor={`baseline-${population.value}`}
              label={population.label}
              radioValue={population.value}
              value={baselineSelected ?? undefined}
              name="baseline-population"
              onChange={(value) => {
                if (value) toggleBaseline(value);
              }}
              customClass="!mb-0"
            />
          ))}
        </div>
      </div>

      {activeCategories.length > 0 && (
        <div className="flex flex-col gap-3 mt-4">
          {activeCategories.map((category: Category) => (
            <CustomLogicRow
              key={category.category}
              category={category}
              selectedOptionId={optionByCategory[category.category]}
              onSetOption={setOption}
              onRemove={removeCategory}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default PopulationsTabContent;
